import styles from 'css/src/components/common/public-pages/PublicPageHeader.module.scss';

import React, { useEffect, useRef } from 'react';
import Immutable from 'immutable';
import classNames from 'classnames';

import { Urls } from 'js/src/client';
import {
    BUTTON_COLORS,
    InlineLink,
    PrimaryButton,
    SecondaryButton,
} from 'js/src/components/common/ui/Button';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import { DEMO_URL, ImmutableFromJS } from 'js/src/libs/consts';
import { getServerData } from 'js/src/libs/server-data';

const UserDropdownToggleProps = ImmutableFromJS([
    {
        text: 'Dashboard',
        id: 'header-dashboard',
        href: Urls.DASHBOARD,
    },
    {
        text: 'Account settings',
        id: 'header-account-settings',
        href: Urls.ACCOUNT,
    },
    {
        text: 'Logout',
        id: 'header-logout',
        href: Urls.LOGOUT,
    },
]);
type Props = {
    backgroundColor?: string;
    invertTextColor?: boolean;
};

const PublicPageHeader = React.memo(function PublicPageHeader({
    backgroundColor,
    invertTextColor = false,
}: Props) {
    const navBarRef = useRef<HTMLElement | null>(null);

    const style: Record<string, string> = {};
    if (backgroundColor) {
        style['backgroundColor'] = backgroundColor;
    }

    let textColorClass = 'color-grey-800 fw-semibold';
    let linkTextColorClass = ''; // Defaults to the button's text color
    let logoImageSrc =
        'https://cdn.lumen5.com/lumen5-site-images/L5-logo/2024/Lumen5-PrimaryLogo.svg';
    let navbarShadeClass = 'navbar-light';

    if (invertTextColor) {
        textColorClass = 'color-grey-200';
        linkTextColorClass = textColorClass;
        logoImageSrc =
            'https://cdn.lumen5.com/lumen5-site-images/lumen5-logo/Lumen5-Single-ReverseColor.webp';
        navbarShadeClass = 'navbar-dark';
    }

    const createNode = (
        <li className="nav-item dropdown me-l5-lg-8">
            <a
                className={`nav-link text-decoration-none font-l5 font-l5--regular ${textColorClass} text-nowrap`}
                href="#"
                id="create-dropdown"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
            >
                Features
                <i className="material-symbols-outlined align-center expand_more">expand_more</i>
            </a>
            <div className="dropdown-menu features-dropdown-menu" aria-labelledby="create-dropdown">
                <div className="d-flex">
                    <div className="features-dropdown-column">
                        <h6 className="dropdown-header">Workflows</h6>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/blog-to-video"
                        >
                            Blog to Video
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/automatic-video-translation"
                        >
                            Automatic Translation
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/idea-to-video"
                        >
                            Turn Ideas to Videos
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/collaborate-review-approve"
                        >
                            Video Collaboration
                        </a>
                    </div>
                    <div className="features-dropdown-column">
                        <h6 className="dropdown-header">AI Generation</h6>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/ai-video-generator"
                        >
                            AI Video Generator
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/ai-voiceover-video-generator"
                        >
                            AI Voice Generator
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/ai-image-generator"
                        >
                            AI Image Generator
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/video-clip-maker"
                        >
                            AI Clips Generator
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/ai-script-writing"
                        >
                            AI Script Writing
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/video-blueprints"
                        >
                            AI Video Blueprints
                        </a>
                    </div>
                    <div className="features-dropdown-column">
                        <h6 className="dropdown-header">Video Editing</h6>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/talking-head"
                        >
                            Talking Head Videos
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/in-app-recording"
                        >
                            In-App Recording
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/screen-recorder"
                        >
                            Screen Recording
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/video-branding-customization"
                        >
                            Custom Branding
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/media-library"
                        >
                            Media Library
                        </a>
                        <a
                            className="dropdown-item"
                            href="https://lumen5.com/features/video-templates-scene-designs"
                        >
                            Video Templates
                        </a>
                    </div>
                </div>
            </div>
        </li>
    );

    const userDropdownToggleProps = React.useMemo(() => {
        return Immutable.fromJS({
            text: getServerData()?.USER?.first_name || getServerData()?.USER?.email,
            color: BUTTON_COLORS.SIMPLE,
            iconLeft: (
                <i
                    className={classNames(
                        'material-symbols-outlined rounded-circle border me-l5-4',
                        textColorClass,
                    )}
                >
                    person
                </i>
            ),
            iconRight: (
                <i
                    className={classNames(
                        'material-symbols-outlined align-center ms-l5-4 expand_more',
                        textColorClass,
                    )}
                >
                    expand_more
                </i>
            ),
            textClassName: textColorClass,
        });
    }, [textColorClass]);

    let userOptionNode = null;
    if (getServerData()?.USER) {
        userOptionNode = (
            <Dropdown
                toggleProps={userDropdownToggleProps}
                itemPropsList={UserDropdownToggleProps}
            />
        );
    } else {
        userOptionNode = (
            <>
                <div className="d-none d-lg-block">
                    <InlineLink
                        href={DEMO_URL}
                        text={
                            <span className={linkTextColorClass}>
                                {'Request' + '\xa0' + 'a' + '\xa0' + 'demo'}
                            </span>
                        }
                        className={'h-100'}
                    />
                </div>
                <div className="d-none d-lg-block">
                    <span
                        className={classNames(
                            'mx-l5-16 d-block align-content-center h-100',
                            linkTextColorClass,
                        )}
                    >
                        |
                    </span>
                </div>
                <div className="d-none d-lg-block">
                    <InlineLink
                        data-testid="header-signin-button"
                        href={Urls.LOGIN}
                        text={<span className={linkTextColorClass}>Log in</span>}
                        className={classNames(
                            'h-100 me-l5-lg-24 font-color-override',
                            linkTextColorClass,
                        )}
                    />
                </div>
                <div className="mb-l5-12 d-md-block d-lg-none border-bottom">
                    <a
                        className={`nav-link text-decoration-none ${textColorClass} font-l5 font-l5--regular`}
                        href={DEMO_URL}
                    >
                        Request a demo
                    </a>
                </div>
                <div className="d-block">
                    <PrimaryButton
                        data-testid="header-signup-button"
                        href={Urls.SIGNUP}
                        text={'Sign' + '\xa0' + 'up'}
                    />
                </div>
                <div className="mt-l5-8 mb-l5-16 d-md-block d-lg-none d-block">
                    <SecondaryButton href={Urls.LOGIN} text="Log in" />
                </div>
            </>
        );
    }

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        const collapseElement = document.getElementById('collapsedBarId');
        if (!collapseElement) return;

        const handleShow = () => {
            // Prevent body scroll when menu is open
            document.body.style.overflow = 'hidden';
        };

        const handleHide = () => {
            // Restore body scroll when menu is closed
            document.body.style.overflow = '';
        };

        // Bootstrap 5 collapse events
        collapseElement.addEventListener('shown.bs.collapse', handleShow);
        collapseElement.addEventListener('hidden.bs.collapse', handleHide);

        return () => {
            collapseElement.removeEventListener('shown.bs.collapse', handleShow);
            collapseElement.removeEventListener('hidden.bs.collapse', handleHide);
            // Clean up: restore body scroll on unmount
            document.body.style.overflow = '';
        };
    }, []);

    return (
        <>
            <nav
                className={`${styles['navbar']} navbar navbar-expand-lg fixed-top ${navbarShadeClass} header-container py-l5-24 px-l5-16 px-l5-lg-48`}
                style={style}
                ref={navBarRef}
            >
                <div className="d-flex ps-l5-lg-24 col-auto col-lg-2 align-items-center">
                    <a href={Urls.HOME} className="navbar-brand p-l5-0 d-flex align-items-center">
                        <img
                            className={`rounded float-start ${styles['nav-bar__logo']}`}
                            src={logoImageSrc}
                            alt="Lumen5 Logo"
                            loading="eager"
                            fetchPriority="high"
                        />
                    </a>
                </div>
                <button
                    className="navbar-toggler border-0"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapsedBarId"
                    aria-controls="collapsedBarId"
                    aria-expanded="false"
                    aria-label="Toggle navigation"
                >
                    <span className="navbar-toggler-icon"></span>
                </button>

                <div className="collapse navbar-collapse align-items-center" id="collapsedBarId">
                    <div className="d-flex align-items-center">
                        <ul className="navbar-nav align-items-center">
                            <li className="nav-item me-l5-lg-8">
                                <a
                                    className={`nav-link text-decoration-none ${textColorClass} font-l5 font-l5--regular`}
                                    href={Urls.PRICING}
                                >
                                    Pricing
                                </a>
                            </li>
                            {createNode}
                            <li className="nav-item me-l5-lg-8 dropdown">
                                <a
                                    className={`nav-link text-nowrap text-decoration-none font-l5 font-l5--regular ${textColorClass}`}
                                    href="#"
                                    id="enterprise-dropdown"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                >
                                    Enterprise
                                    <i className="material-symbols-outlined align-center expand_more">
                                        expand_more
                                    </i>
                                </a>
                                <div
                                    className="dropdown-menu enterprise-dropdown-menu"
                                    aria-labelledby="enterprise-dropdown"
                                >
                                    <div className="d-flex">
                                        <div className="enterprise-dropdown-column">
                                            <h6 className="dropdown-header">Scale up</h6>
                                            <a className="dropdown-item" href="/enterprise">
                                                Lumen5 for Enterprise
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://fast.wistia.com/embed/channel/d92lo5wcg8"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Branded Templates
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://ps.lumen5.com"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Professional Services
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href={DEMO_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Request a Demo
                                            </a>
                                        </div>
                                        <div className="enterprise-dropdown-column">
                                            <h6 className="dropdown-header">Discover</h6>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/case-study"
                                            >
                                                Case Studies
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/use-cases"
                                            >
                                                Use Cases
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/integrations"
                                            >
                                                Integrations
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </li>
                            <li className="nav-item me-l5-lg-8 dropdown">
                                <a
                                    className={`nav-link text-nowrap text-decoration-none font-l5 font-l5--regular ${textColorClass}`}
                                    href="#"
                                    id="solutions-dropdown"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                >
                                    Solutions
                                    <i className="material-symbols-outlined align-center expand_more">
                                        expand_more
                                    </i>
                                </a>
                                <div
                                    className="dropdown-menu solutions-dropdown-menu"
                                    aria-labelledby="solutions-dropdown"
                                >
                                    <div className="d-flex">
                                        <div className="solutions-dropdown-column">
                                            <h6 className="dropdown-header">Industry</h6>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/professional-services"
                                            >
                                                Professional Service Firms
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/tech-telecom"
                                            >
                                                Technology & Telecom
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/manufacturing"
                                            >
                                                Manufacturing
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/banking-financial"
                                            >
                                                Banking & Financial
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/oil-gas-mining"
                                            >
                                                Oil, Gas & Mining
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/media-publishing"
                                            >
                                                Media & Publishing
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/government-healthcare-nonprofit"
                                            >
                                                Government, Health, NGOs
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/education-university-college"
                                            >
                                                Colleges & Universities
                                            </a>
                                        </div>
                                        <div className="solutions-dropdown-column">
                                            <h6 className="dropdown-header">Use Case</h6>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/ai-video-marketing-platform"
                                            >
                                                Marketing
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/ai-learning-development"
                                            >
                                                Learning & Development
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/internal-comms-video-platform"
                                            >
                                                Internal Communications
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/ai-linkedin-video-generator"
                                            >
                                                LinkedIn Videos
                                            </a>
                                            <a
                                                className="dropdown-item"
                                                href="https://lumen5.com/solutions/ai-instagram-video-generator"
                                            >
                                                Instagram Videos
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </li>
                            <li className="nav-item me-l5-lg-8 dropdown">
                                <a
                                    className={`nav-link text-nowrap text-decoration-none font-l5 font-l5--regular ${textColorClass}`}
                                    href="#"
                                    id="learn-dropdown"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                >
                                    Learn
                                    <i className="material-symbols-outlined align-center expand_more">
                                        expand_more
                                    </i>
                                </a>
                                <div className="dropdown-menu" aria-labelledby="learn-dropdown">
                                    <a
                                        className="dropdown-item"
                                        href="/learn/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Blog
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://pod.co/supercharge-marketing"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Podcast
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="/learn/resources"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Resources
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://help.lumen5.com/en/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Knowledge Base
                                    </a>
                                    <a
                                        className="dropdown-item"
                                        href="https://lumen5.thinkific.com/courses/beginnersblueprint"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Online Video Course
                                    </a>
                                </div>
                            </li>
                            <li className="nav-item mb-l5-12 d-block d-lg-none border-bottom">
                                <a
                                    className={`nav-link text-decoration-none ${textColorClass} font-l5 font-l5--regular`}
                                    href={DEMO_URL}
                                >
                                    Request a demo
                                </a>
                            </li>
                            <li className="nav-item d-block d-lg-none">
                                <PrimaryButton href={Urls.SIGNUP} text={'Sign' + '\xa0' + 'up'} />
                            </li>
                            <li className="nav-item mt-l5-8 mb-l5-16 d-block d-lg-none">
                                <SecondaryButton href={Urls.LOGIN} text="Log in" />
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="justify-content-end d-none d-lg-block">
                    <div className="d-flex h-100 align-items-center">{userOptionNode}</div>
                </div>
                <div className={styles['header-gradient-border']} />
            </nav>
        </>
    );
});

export default PublicPageHeader;
